<template>
  <div class="min-h-screen bg-off-black text-white">
    <div class="container mx-auto px-4 py-16 max-w-4xl">
      <!-- Header Section -->
      <div class="text-center mb-12">
        <h1 class="text-4xl md:text-5xl font-serif font-semibold mb-6 text-vibe-green">
          Smart Onboarding for Australian Professionals
        </h1>
        <p class="text-xl md:text-2xl text-gray-green mb-4">
          Download your practical guide to AML compliance, risk management, and onboarding efficiency — tailored for the Australian market.
        </p>
        <p class="text-lg text-gray-green">
          Specifically designed for legal and accounting professionals operating in Australia.
        </p>
      </div>

      <!-- Success Message -->
      <div v-if="submitted" class="bg-green-600 border border-vibe-green rounded-lg p-8 mb-8 text-center">
        <h2 class="text-2xl font-semibold mb-4 text-vibe-green">Thank you!</h2>
        <p class="text-lg mb-4">Please check your email for the download link.</p>
        <p class="text-gray-green">The guide will be in your inbox shortly.</p>
      </div>

      <!-- Lead Capture Form -->
      <div v-if="!submitted" class="bg-green-700 rounded-lg p-8 md:p-12 shadow-2xl border border-green-600">
        <form @submit.prevent="handleSubmit" class="space-y-6">
          <div>
            <label for="name" class="block text-sm font-semibold mb-2 text-vibe-green">
              Full Name <span class="text-error-red">*</span>
            </label>
            <input type="text" id="name" v-model="formData.name" required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your full name" />
          </div>

          <div>
            <label for="organisation" class="block text-sm font-semibold mb-2 text-vibe-green">
              Organisation Name <span class="text-error-red">*</span>
            </label>
            <input type="text" id="organisation" v-model="formData.organisation" required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your organisation name" />
          </div>

          <div>
            <label for="phone" class="block text-sm font-semibold mb-2 text-vibe-green">
              Phone Number <span class="text-error-red">*</span>
            </label>
            <input type="tel" id="phone" v-model="formData.phone" required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your phone number" />
          </div>

          <div>
            <label for="email" class="block text-sm font-semibold mb-2 text-vibe-green">
              Email Address <span class="text-error-red">*</span>
            </label>
            <input type="email" id="email" v-model="formData.email" required
              class="w-full px-4 py-3 rounded-lg bg-green-800 border border-green-600 text-white placeholder-gray-green focus:outline-none focus:border-vibe-green transition-colors"
              placeholder="Enter your email address" />
          </div>

          <div class="flex items-start">
            <input type="checkbox" id="optIn" v-model="formData.optIn" required
              class="mt-1 w-5 h-5 rounded border-green-600 bg-green-800 text-vibe-green focus:ring-vibe-green focus:ring-2" />
            <label for="optIn" class="ml-3 text-sm text-gray-green">
              I agree to receive updates and relevant insights from Marker AI about compliance and onboarding solutions. <span class="text-error-red">*</span>
            </label>
          </div>

          <div v-if="errorMessage" class="bg-error-red bg-opacity-20 border border-error-red rounded-lg p-4">
            <p class="text-error-red text-sm">{{ errorMessage }}</p>
          </div>

          <button type="submit" :disabled="isSubmitting"
            class="w-full btn-primary text-lg font-semibold py-4 transition-all duration-300 hover:bg-swamp-green hover:text-white disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmitting ? 'Submitting...' : 'Download the Guide' }}
          </button>

          <p class="text-xs text-gray-green text-center mt-6">
            We respect your privacy. Your details will be used to share relevant updates and compliance insights. You can unsubscribe at any time.
          </p>
        </form>
      </div>

      <!-- What's Inside Section -->
      <div v-if="!submitted" class="mt-12 bg-green-800 rounded-lg p-8 border border-green-600">
        <h2 class="text-2xl font-serif font-semibold mb-6 text-vibe-green text-center">
          What's Inside Your Guide
        </h2>
        <div class="grid md:grid-cols-2 gap-4 text-gray-green">
          <div class="flex items-start">
            <span class="text-vibe-green mr-3 text-xl">✓</span>
            <span>Risk-based onboarding strategies</span>
          </div>
          <div class="flex items-start">
            <span class="text-vibe-green mr-3 text-xl">✓</span>
            <span>Sector-specific insights for the Australian market</span>
          </div>
          <div class="flex items-start">
            <span class="text-vibe-green mr-3 text-xl">✓</span>
            <span>Practical tips on OSINT, HUMINT, and document curation</span>
          </div>
          <div class="flex items-start">
            <span class="text-vibe-green mr-3 text-xl">✓</span>
            <span>A forward look at ECCTA reforms and onboarding automation</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AustraliaSmartOnboardingDownload',
  data() {
    return {
      formData: { name: '', organisation: '', phone: '', email: '', optIn: false },
      isSubmitting: false,
      submitted: false,
      errorMessage: '',
    };
  },
  methods: {
    async handleSubmit() {
      this.isSubmitting = true;
      this.errorMessage = '';
      try {
        const response = await axios.post('/api/submit-australia-smart-onboarding-form', this.formData);
        if (response.data.success) {
          this.submitted = true;
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        console.error('Form submission error:', error);
        if (error.response?.data?.message) {
          this.errorMessage = error.response.data.message;
        } else {
          this.errorMessage = 'There was an error submitting your form. Please try again.';
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
